// src/pages/DescriptionPageCompetition.jsx
import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import "./Description.css";
import { FaArrowLeft } from "react-icons/fa";
import BottomNav from "./BottomNav";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Filter } from "bad-words";
import { notificationService } from '../utils/notificationService';

const DescriptionPageCompetition = () => {
  const [description, setDescription] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [hasProfanity, setHasProfanity] = useState(false);
  const [uid, setUid] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const filter = new Filter();

  const { title, tags } = location.state || {};

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => setUid(user ? user.uid : null));
    return unsub;
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setHasProfanity(filter.isProfane(val));
    if (val.length <= 3000) {
      setDescription(val);
      e.target.style.height = "auto";
      e.target.style.height = e.target.scrollHeight + "px";
      if (val.trim()) setShowWarning(false);
    }
  };

  useEffect(() => {
    if (!title || !tags) {
      notificationService.showWarning("Session expired or missing data. Please restart your post.");
    }
  }, [title, tags]);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!title || !tags) {
      notificationService.showError("Missing Title or Tags. Please go back and restart.");
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 2500);
      return;
    }
    if (description.trim() === "") {
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 2500);
      return;
    }
    if (description.trim().length < 200) {
      setShowWarning(true);
      notificationService.showWarning("Description must be at least 200 characters long.");
      return;
    }
    if (hasProfanity) {
      setShowWarning(true);
      return;
    }
    if (!uid) {
      notificationService.showWarning("Please sign in to continue.");
      return;
    }

    setIsSubmitting(true);
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timed out. Network slow or blocked.")), 45000)
      );

      await Promise.race([
        addDoc(collection(db, "CompetitionIdeas"), {
          title,
          tags,
          description,
          uid,
          createdAt: serverTimestamp(),
        }),
        timeoutPromise
      ]);

      navigate("/UploadImageCompetition", {
        state: { description, title, tags },
      });
    } catch (err) {
      console.error(err);
      notificationService.showError(err.message || "Failed to save. Try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="desc-page">
      <header className="desc-header">
        <button
          type="button"
          className="desc-back-btn"
          aria-label="Go back"
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft />
        </button>
        <h1 className="desc-page-title">Description</h1>
      </header>

      <main className="desc-sheet">
        <div className="desc-title-tag" aria-hidden="false">
          <span className="desc-title-tag-label">Idea</span>
          {title || "Untitled"}
        </div>

        <label className="sr-only" htmlFor="description-textarea">
          Description of your idea
        </label>

        <div className="desc-textarea-wrap">
          <textarea
            id="description-textarea"
            className="desc-textarea"
            placeholder="Tell people what problem this solves, how it works, and why it matters..."
            value={description}
            onChange={handleChange}
            rows={1}
          />
        </div>

        <div className="desc-char-count">
          {description.length} / 3000{" "}
          <span className={description.length >= 200 ? "text-success" : "text-warning"}>
            (minimum 200 characters)
          </span>
        </div>

        {showWarning && (
          <div className="desc-warning-note">
            {description.trim() === ""
              ? "Description cannot be empty!"
              : "Description must be at least 200 characters long!"}
          </div>
        )}

        {hasProfanity && (
          <div className="desc-warning-note">Your input contains inappropriate language.</div>
        )}

        {showPopup && <div className="desc-warning-note">Please complete the fields.</div>}

        {description.trim().length >= 200 && !hasProfanity && (
          <button
            className="desc-continue-btn"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Processing…" : "Continue"}
          </button>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default DescriptionPageCompetition;